import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { waysApi, type TechEvaluation, type TechTool, type TechnologyStandardAnswer } from '../../services/waysApi';

/**
 * Fit-for-purpose evaluations (PO-92 EP-10, PO92-361; Zane, 2026-10-29: "evaluate against alternatives
 * and determin why we chose the one"). Each is filed on the epic or feature that needs the tool: the
 * need, the candidates in tier order (open source, NVIDIA Inception, others) scored against the
 * standard's criteria, the choice, and why every alternative lost. Ways validates and keeps them.
 */
const TIER_LABEL: Record<TechTool['tier'], string> = {
  oss: 'Open source', nvidia: 'NVIDIA Inception', commercial: 'Commercial', internal: 'Internal',
};
type Std = TechnologyStandardAnswer['standard'];

export default function TechEvaluations({ std, onTool }: { std: Std; onTool: (id: string) => void }) {
  const [list, setList] = useState<TechEvaluation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filing, setFiling] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    waysApi.technologyEvaluations().then((r) => { setList(r.evaluations); setError(null); })
      .catch((e) => setError((e as Error).message || 'Ways could not be reached'));
  }, [tick]);

  return (
    <section className="p-4 space-y-3" role="tabpanel" aria-label="Evaluations">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-xs text-slate-600 dark:text-slate-300 max-w-prose">
          Why each tool was chosen against the alternatives, filed on the epic or feature that needed it. Candidates are
          compared open source first, then NVIDIA Inception, then others; every alternative says why it lost.
        </p>
        <button type="button" onClick={() => setFiling(true)}
          className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-md bg-accent text-white">
          <Plus className="w-3.5 h-3.5" aria-hidden="true" /> File an evaluation
        </button>
      </div>
      {error ? <p className="text-sm text-red-600 dark:text-red-400">Evaluations could not be loaded: {error}</p>
        : !list ? <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
          : list.length ? <ul className="space-y-3">{list.map((e) => <EvaluationCard key={e.id} e={e} std={std} onTool={onTool} />)}</ul>
            : <p className="text-sm text-slate-500">No evaluation is filed yet.</p>}
      {filing ? <FileDialog std={std} onClose={() => setFiling(false)} onDone={() => { setFiling(false); setTick((t) => t + 1); }} /> : null}
    </section>
  );
}

function EvaluationCard({ e, std, onTool }: { e: TechEvaluation; std: Std; onTool: (id: string) => void }) {
  const crit = std.criteria;
  return (
    <li className="px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 text-sm">
      <div className="text-slate-900 dark:text-slate-100 font-medium">{e.need}</div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
        {e.categoryName} · on <Link to={`/item/${e.work}`} className="text-accent hover:underline">{e.workTitle || e.work}</Link>
        {e.projectKey ? ` · ${e.projectKey} ${e.projectName || ''}` : ''} · {new Date(e.at * 1000).toLocaleDateString()}
      </p>
      <p className="mt-1.5 text-xs text-slate-700 dark:text-slate-300">
        <span className="font-medium text-slate-900 dark:text-slate-100">Chose {e.choiceName}</span> ({TIER_LABEL[e.choiceTier]}): {e.why}
        {e.laterTier ? <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">over an earlier-tier candidate</span> : null}
      </p>
      <div className="mt-2 overflow-x-auto">
        <table className="min-w-[560px] w-full text-xs">
          <thead>
            <tr className="text-left text-slate-500">
              <th className="px-2 py-1">Candidate</th><th className="px-2 py-1">Tier</th>
              {crit.map((c) => <th key={c.id} className="px-2 py-1 text-center" title={c.question}>{c.name}</th>)}
              <th className="px-2 py-1">Why not</th>
            </tr>
          </thead>
          <tbody>
            {e.candidates.map((c) => {
              const chosen = (c.tool || c.name) === e.choice;
              return (
                <tr key={c.tool || c.name} className={clsx('border-t border-slate-200 dark:border-slate-700', chosen && 'bg-emerald-50/60 dark:bg-emerald-900/20')}>
                  <td className="px-2 py-1 text-slate-900 dark:text-slate-100">
                    {c.tool ? <button type="button" onClick={() => onTool(c.tool!)} className="hover:text-accent hover:underline text-left">{c.name}</button> : c.name}
                    {chosen ? <span className="ml-1 text-emerald-700 dark:text-emerald-300">(chosen)</span> : null}
                  </td>
                  <td className="px-2 py-1 text-slate-600 dark:text-slate-300 whitespace-nowrap">{TIER_LABEL[c.tier]}</td>
                  {crit.map((k) => <td key={k.id} className="px-2 py-1 text-center tabular-nums text-slate-700 dark:text-slate-300">{c.scores[k.id] ?? '–'}</td>)}
                  <td className="px-2 py-1 text-slate-600 dark:text-slate-300">{chosen ? '' : c.whyNot}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </li>
  );
}

interface Row { tool: string; name: string; tier: string; scores: Record<string, number>; whyNot: string }
const blank = (): Row => ({ tool: '', name: '', tier: 'oss', scores: {}, whyNot: '' });

/** File one evaluation. Ways checks it (two candidates at least, scores 1-5, every loser says why). */
function FileDialog({ std, onClose, onDone }: { std: Std; onClose: () => void; onDone: () => void }) {
  const [work, setWork] = useState('');
  const [need, setNeed] = useState('');
  const [category, setCategory] = useState(std.categories[0]?.id || '');
  const [rows, setRows] = useState<Row[]>([blank(), blank()]);
  const [choice, setChoice] = useState(0);
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const field = 'w-full px-2 py-1 text-sm rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100';
  const set = (i: number, patch: Partial<Row>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const label = (r: Row) => (r.tool ? std.tools.find((t) => t.id === r.tool)?.name : r.name) || 'this candidate';

  const submit = async () => {
    setBusy(true);
    try {
      await waysApi.fileEvaluation({
        work: work.trim(), need: need.trim(), category, why: why.trim(),
        choice: rows[choice].tool || rows[choice].name.trim(),
        candidates: rows.map((r, i) => ({
          ...(r.tool ? { tool: r.tool } : { name: r.name.trim(), tier: r.tier }),
          scores: r.scores, ...(i === choice ? {} : { whyNot: r.whyNot.trim() }),
        })),
      });
      toast.success('Evaluation filed');
      onDone();
    } catch (e) { toast.error((e as Error).message || 'Could not file it', { duration: 8000 }); }
    finally { setBusy(false); }
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="eval-title" className="fixed inset-0 z-50 bg-black/40 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-lg bg-white dark:bg-slate-800 shadow-xl p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 id="eval-title" className="text-sm font-semibold text-slate-900 dark:text-slate-100">File a fit-for-purpose evaluation</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs text-slate-600 dark:text-slate-300" htmlFor="ev-work">Epic or feature (id)</label>
            <input id="ev-work" className={field} placeholder="feature:…" value={work} onChange={(e) => setWork(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-slate-600 dark:text-slate-300" htmlFor="ev-cat">Category</label>
            <select id="ev-cat" className={field} value={category} onChange={(e) => setCategory(e.target.value)}>
              {std.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs text-slate-600 dark:text-slate-300" htmlFor="ev-need">The need: what capability the work requires</label>
          <input id="ev-need" className={field} value={need} onChange={(e) => setNeed(e.target.value)} />
        </div>
        <fieldset className="space-y-2">
          <legend className="text-xs text-slate-600 dark:text-slate-300">Candidates: open source first, then NVIDIA Inception, then others. Score each 1–5; pick the one chosen.</legend>
          {rows.map((r, i) => (
            <div key={i} className="rounded-md border border-slate-200 dark:border-slate-700 p-2 space-y-2">
              <div className="grid gap-2 sm:grid-cols-[auto_minmax(0,1fr)_auto] items-center">
                <label className="flex items-center gap-1 text-xs text-slate-700 dark:text-slate-200">
                  <input type="radio" name="ev-choice" checked={choice === i} onChange={() => setChoice(i)} /> Chosen
                </label>
                <div className="flex gap-2">
                  <label className="sr-only" htmlFor={`ev-tool-${i}`}>Tool</label>
                  <select id={`ev-tool-${i}`} className={field} value={r.tool} onChange={(e) => set(i, { tool: e.target.value })}>
                    <option value="">Not in the catalogue…</option>
                    {std.tools.map((t) => <option key={t.id} value={t.id}>{t.name} ({TIER_LABEL[t.tier]})</option>)}
                  </select>
                  {!r.tool ? (
                    <>
                      <label className="sr-only" htmlFor={`ev-name-${i}`}>Name</label>
                      <input id={`ev-name-${i}`} className={field} placeholder="Name" value={r.name} onChange={(e) => set(i, { name: e.target.value })} />
                      <label className="sr-only" htmlFor={`ev-tier-${i}`}>Tier</label>
                      <select id={`ev-tier-${i}`} className={field} value={r.tier} onChange={(e) => set(i, { tier: e.target.value })}>
                        {Object.entries(TIER_LABEL).map(([id, l]) => <option key={id} value={id}>{l}</option>)}
                      </select>
                    </>
                  ) : null}
                </div>
                <button type="button" onClick={() => { setRows((rs) => rs.filter((_, j) => j !== i)); setChoice((c) => (c >= i && c > 0 ? c - 1 : c)); }}
                  disabled={rows.length <= 2} aria-label={`Remove ${label(r)}`}
                  className="p-1 rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="flex flex-wrap gap-2">
                {std.criteria.map((c) => (
                  <label key={c.id} className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300" title={c.question}>
                    {c.name}
                    <select className="px-1 py-0.5 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                      value={r.scores[c.id] ?? ''} onChange={(e) => {
                        const v = e.target.value; const scores = { ...r.scores };
                        if (v) scores[c.id] = Number(v); else delete scores[c.id];
                        set(i, { scores });
                      }}>
                      <option value="">–</option>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              {choice !== i ? (
                <div>
                  <label className="sr-only" htmlFor={`ev-whynot-${i}`}>Why {label(r)} was not chosen</label>
                  <input id={`ev-whynot-${i}`} className={field} placeholder={`Why ${label(r)} was not chosen`} value={r.whyNot}
                    onChange={(e) => set(i, { whyNot: e.target.value })} />
                </div>
              ) : null}
            </div>
          ))}
          <button type="button" onClick={() => setRows((rs) => [...rs, blank()])} className="inline-flex items-center gap-1 text-xs text-accent hover:underline">
            <Plus className="w-3.5 h-3.5" aria-hidden="true" /> Add a candidate
          </button>
        </fieldset>
        <div>
          <label className="block text-xs text-slate-600 dark:text-slate-300" htmlFor="ev-why">Why the chosen one won</label>
          <textarea id="ev-why" rows={3} className={field} value={why} onChange={(e) => setWhy(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1 text-xs rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200">Cancel</button>
          <button type="button" onClick={submit} disabled={busy || !work.trim() || !need.trim() || !why.trim()}
            className="px-3 py-1 text-xs rounded-md bg-accent text-white disabled:opacity-50">File it</button>
        </div>
      </div>
    </div>
  );
}
